import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/** API injectee par le script Google sur window. */
declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
    };
  }
}

const SCRIPT_ID = 'google-recaptcha-script';

/**
 * Valeur laissee dans les environnements ou aucune cle n'est configuree.
 *
 * Tant qu'elle est en place, aucun jeton n'est demande : l'API, elle, n'exige
 * rien tant que RECAPTCHA_SECRET_KEY est absente cote serveur. Les formulaires
 * restent donc utilisables, simplement non proteges.
 */
const PLACEHOLDER_KEY = 'REMPLACER_PAR_LA_CLE_DE_SITE_RECAPTCHA';

/** En-tete lu par l'intercepteur anti-bot de l'API. */
export const RECAPTCHA_HEADER = 'X-Recaptcha-Token';

/**
 * reCAPTCHA v3 : aucun widget, aucune interaction du client.
 *
 * Le jeton est demande au moment de la soumission, jamais au chargement : il
 * n'est valable que 2 minutes, et un client qui remplit tranquillement un
 * formulaire de devis met souvent plus longtemps que ca.
 *
 * L'application est rendue cote serveur : tout ce qui touche `document` ou
 * `window` doit rester derriere isPlatformBrowser, sinon le rendu SSR casse.
 */
@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private loading?: Promise<void>;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  get siteKey(): string {
    return (environment as any).recaptcha?.siteKey ?? '';
  }

  /** false en SSR, ou si aucune cle de site exploitable n'est configuree. */
  get available(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      !!this.siteKey &&
      this.siteKey !== PLACEHOLDER_KEY
    );
  }

  /**
   * Renvoie un jeton frais pour cette action, ou '' si la verification ne peut
   * pas avoir lieu (cle absente, script bloque, rendu serveur).
   *
   * Ne rejette jamais : c'est au serveur de decider quoi faire d'une
   * soumission sans jeton, pas au navigateur de bloquer un client.
   */
  async execute(action: string): Promise<string> {
    if (!this.available) {
      return '';
    }
    try {
      await this.load();
      return await new Promise<string>((resolve, reject) => {
        window.grecaptcha!.ready(() => {
          window
            .grecaptcha!.execute(this.siteKey, { action })
            .then(resolve)
            .catch(reject);
        });
      });
    } catch {
      // Bloqueur de contenu, reseau coupe, quota Google depasse : on laisse
      // partir la demande sans jeton plutot que de perdre le client.
      return '';
    }
  }

  private load(): Promise<void> {
    if (window.grecaptcha?.execute) {
      return Promise.resolve();
    }
    if (this.loading) {
      return this.loading;
    }

    this.loading = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(
        SCRIPT_ID,
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () =>
          reject(new Error('reCAPTCHA script failed to load')),
        );
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      // La cle de site fait partie de l'URL en v3, contrairement a la v2.
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
        this.siteKey,
      )}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Rejouable : un blocage ponctuel ne doit pas condamner la page.
        this.loading = undefined;
        reject(new Error('reCAPTCHA script failed to load'));
      };
      document.head.appendChild(script);
    });

    return this.loading;
  }
}

/**
 * En-tetes a joindre a une soumission de formulaire.
 *
 * Le jeton part en en-tete plutot que dans le corps : aucun DTO de devis n'a
 * besoin d'un champ supplementaire, et l'API le controle en un seul endroit.
 */
export function recaptchaHeaders(token?: string | null): HttpHeaders {
  let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  if (token) {
    headers = headers.set(RECAPTCHA_HEADER, token);
  }
  return headers;
}
